import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Breadcrumb } from '@/components/breadcrumb/Breadcrumb'
import type { Crumb } from '@/components/breadcrumb/Breadcrumb'
import { EditPageShell } from '@/components/page-shell/EditPageShell'
import { Button, LoadingSection, PlusIcon, showToast, TrashIcon } from '@quickmd-llc/design-system'
import type { ServiceTypeAdminApiInContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { ServiceInfoForm } from '../view-service/components/service-info-form/ServiceInfoForm'
import { VisitTypeForm } from '../view-service/components/visit-type-form/VisitTypeForm'
import type { VisitTypeFormState } from '../view-service/components/visit-type-form/VisitTypeForm'
import { DeleteVisitTypePopup } from '../view-service/components/DeleteVisitTypePopup/DeleteVisitTypePopup'
import { SupportedStatesInteractive } from '../../components/supported-states-display/SupportedStatesInteractive'
import { SHOW_SERVICE_SUPPORTED_STATES } from '../../constants/unreleasedServiceFeatures'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import { formatMoney } from '../../utils/common'
import { reset, toggleCancelPressed } from '../../store/newService/NewServiceActions'
import { usePublishService } from './hooks/usePublishService'
import { useIntakeFormTemplates } from '../../hooks/useIntakeFormTemplates'
import { useConsentForms } from '../../hooks/useConsentForms'
import { maxTextLength, text, toastErrorsList } from './constants'
import { checkServiceTypesTables } from './helpers/checkServiceTypesTables'
import type { NewServiceVisitType } from './types'
import { normalizeIntakeForms } from '@/utils/narrowing'

const emptyVisitTypeErrors = {
  visitLengthError: '',
  visitLengthLocalError: '',
  coastUSDError: '',
  coastUSDLocalError: '',
  cancellationFeeError: '',
  cancellationFeeLocalError: '',
  visitNameError: '',
  visitNameLocalError: '',
  visitDescriptionError: '',
  visitDescriptionLocalError: '',
  consentFormError: '',
  consentFormLocalError: '',
  visibilityTypeError: '',
} as const

const createEmptyVisitType = (): NewServiceVisitType => ({
  VisitTypeId: crypto.randomUUID(),
  Name: '',
  Description: '',
  Duration: 15,
  Cost: '',
  CancellationFee: '',
  ConsentForms: [],
  IntakeFormId: '',
  VisibilityType: 'all',
  IsMedicationAssistedTreatment: false,
  IsPostBookingReminderRequired: false,
  IsPhoneVisitCommunicationTypeAllowed: true,
  IsInPerson: false,
  IsAsynchronous: false,
  IsNewPatientSpacingEnabled: false,
  errors: { ...emptyVisitTypeErrors },
})

export default function NewService() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { state: locationState } = useLocation()

  const { intakeForms } = useIntakeFormTemplates()
  const { additionalConsentFormsOptions } = useConsentForms()
  const { publishService, isPending: loading } = usePublishService()

  const [error, setError] = useState('')
  const [publishSuccess, setPublishSuccess] = useState(false)
  // Placeholder until the backend adds service-type image upload (see onPublicService TODO); the
  // field never changes, so it is a plain const rather than state.
  const imageUrl = ''
  const [visitTypeToDelete, setVisitTypeToDelete] = useState<string | null>(null)
  const [serviceName, setServiceName] = useState('')
  const [serviceAbbreviation, setServiceAbbreviation] = useState('')
  const [serviceAbbreviationError, setServiceAbbreviationError] = useState('')
  const [description, setDescription] = useState('')
  const [serviceNameError, setServiceNameError] = useState('')
  const [descriptionLocalError, setDescriptionLocalError] = useState('')
  const [isDescriptionError, setIsDescriptionError] = useState(false)
  // SHOW_SERVICE_SUPPORTED_STATES is flag-off, so this stays [] in production. Kept to drive the
  // flag-gated SupportedStatesInteractive section (unchanged); it is not part of the POST contract.
  const [excludedStateCodes, setExcludedStateCodes] = useState<string[]>([])
  const [serviceTypes, setServiceTypes] = useState<NewServiceVisitType[]>([createEmptyVisitType()])

  const normalizedIntakeForms = useMemo(() => normalizeIntakeForms(intakeForms), [intakeForms])

  useEffect(() => {
    setError('')
  }, [])

  useEffect(() => {
    if (publishSuccess) {
      void navigate(-1)
      dispatch(reset())
    }
  }, [publishSuccess, navigate, dispatch])

  const onCreateNewServiceType = useCallback(() => {
    setServiceTypes([...serviceTypes, createEmptyVisitType()])
  }, [serviceTypes])

  const onConfirmDeleteVisitType = useCallback(() => {
    setServiceTypes((prev) => prev.filter((t) => t.VisitTypeId !== visitTypeToDelete))
    setVisitTypeToDelete(null)
  }, [visitTypeToDelete])

  useEffect(() => {
    if (error === getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)) {
      setTimeout(() => {
        setError('')
      }, 3500)
    }
  }, [error])

  // error-state surfaced via toast (replaces the inline ErrorAlert). Same trigger condition as the
  // old inline alert: the error is one of toastErrorsList (NO_CONNECTION / FILL_ALL_FIELDS / DEFAULT).
  // The message text is forwarded verbatim so the validation-error text stays assertable.
  useEffect(() => {
    if (toastErrorsList.includes(error)) {
      showToast({ type: 'error', message: error, position: 'bottom-right' })
    }
  }, [error])

  const onSetServiceName = useCallback(
    (value: string) => {
      setServiceName(value)
      setIsDescriptionError(false)
      if (value.length < 70) {
        setServiceNameError('')
      }
      if (error) setError('')
    },
    [error],
  )

  const onSetServiceAbbreviation = useCallback(
    (value: string) => {
      setServiceAbbreviation(value)
      if (value.length < maxTextLength.SERVICE_ABBREVIATION) {
        setServiceAbbreviationError('')
      }
      if (error) setError('')
    },
    [error],
  )

  const onDescriptionChange = useCallback(
    (value: string) => {
      setDescription(value)
      if (descriptionLocalError) setDescriptionLocalError('')
      if (isDescriptionError) setIsDescriptionError(false)
      if (error) setError('')
    },
    [descriptionLocalError, isDescriptionError, error],
  )

  const onCheckServiceTypesTables = useCallback(
    () => checkServiceTypesTables({ serviceTypes, setError, setServiceTypes }),
    [serviceTypes],
  )

  const onPublicService = useCallback(() => {
    // TODO: when backend adds /api/v1/servicetypes/images, upload the image here
    // and pass the resulting ImageUrl in the publishService body.
    const body: ServiceTypeAdminApiInContract = {
      Name: serviceName.trim(),
      Description: description.trim(),
      Abbreviation: serviceAbbreviation.trim(),
      VisitTypes: serviceTypes.map((serviceType) => {
        const {
          CancellationFee,
          Cost,
          Duration,
          Name,
          Description: visitDescription,
          ConsentForms,
          IntakeFormId,
          VisibilityType,
          IsPostBookingReminderRequired,
          IsMedicationAssistedTreatment,
          IsPhoneVisitCommunicationTypeAllowed,
          IsInPerson,
          IsAsynchronous,
          IsNewPatientSpacingEnabled,
        } = serviceType
        return {
          VisitTypeId: null,
          Name: Name ?? '',
          Description: visitDescription ?? '',
          IntakeFormId: IntakeFormId ?? '',
          IsPostBookingReminderRequired,
          IsMedicationAssistedTreatment,
          IsPhoneVisitCommunicationTypeAllowed,
          IsNewPatientSpacingEnabled,
          Duration: Number(Duration),
          Cost: Number(formatMoney(Cost)),
          CancellationFee: Number(formatMoney(CancellationFee)),
          VisibilityType: VisibilityType ?? '',
          ConsentForms: ConsentForms.filter((t) => t.isActive).map((t) => ({ ConsentFormId: t.id ?? null })),
          IsInPerson,
          IsAsynchronous,
        }
      }),
    }

    publishService(body, {
      onSuccess: () => {
        setPublishSuccess(true)
      },
      onError: (message) => {
        setError(message)
      },
    })
  }, [description, publishService, serviceName, serviceTypes, serviceAbbreviation])

  const onPublishService = useCallback(() => {
    const updatedServiceTypes = onCheckServiceTypesTables()

    // Track field validity in THIS invocation. Previously the submit was gated on a persistent
    // localStorage flag ('viewServiceError') that was set on error but only cleared by a later
    // Create press — so the click that surfaced the error left it set and a second click was
    // needed to submit. Gating on fresh local state fixes the double-press.
    let hasFieldError = false
    if (
      serviceName.trim() === '' ||
      serviceAbbreviation.trim() === '' ||
      description.replaceAll('\n', '').trim() === '' ||
      description.length > maxTextLength.DESCRIPTION ||
      serviceName.length > maxTextLength.SERVICE_NAME ||
      serviceAbbreviation.length > maxTextLength.SERVICE_ABBREVIATION
    ) {
      hasFieldError = true
      if (
        serviceName.trim() === '' &&
        description.replaceAll('\n', '').trim() === '' &&
        serviceAbbreviation.trim() === ''
      ) {
        setIsDescriptionError(true)
        setServiceNameError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, text.SERVICE_NAME))
        setDescriptionLocalError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, text.DESCRIPTION))
        setServiceAbbreviationError(
          getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, text.SERVICE_ABBREVIATION),
        )
        setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
        return
      }
      if (serviceName.trim() === '') {
        setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
        setServiceNameError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, text.SERVICE_NAME))
      }
      if (serviceAbbreviation.trim() === '') {
        setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
        setServiceAbbreviationError(
          getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, text.SERVICE_ABBREVIATION),
        )
      }
      if (serviceAbbreviation.length > maxTextLength.SERVICE_ABBREVIATION) {
        setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
        setServiceAbbreviationError(
          getUserFriendlyErrorMessage(errorCodes.MAX_CHARACTER_LIMIT, null, text.SERVICE_ABBREVIATION),
        )
      }
      if (serviceName.length > maxTextLength.SERVICE_NAME) {
        setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
        setServiceNameError(getUserFriendlyErrorMessage(errorCodes.MAX_CHARACTER_LIMIT, null, text.SERVICE_NAME))
      }
      if (description.length > maxTextLength.DESCRIPTION) {
        setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
        setDescriptionLocalError(getUserFriendlyErrorMessage(errorCodes.MAX_CHARACTER_LIMIT, null, text.DESCRIPTION))
      }
      if (description.replaceAll('\n', '').trim() === '') {
        setError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS, null, text.DESCRIPTION))
        setDescriptionLocalError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, text.DESCRIPTION))
        setIsDescriptionError(true)
      }
    }
    const serviceTypesLength = updatedServiceTypes.length
    let hasErrors = false

    for (let i = 0; serviceTypesLength > i; i += 1) {
      const errors = updatedServiceTypes[i]?.errors

      if (errors) {
        const errorCount = Object.values(errors).some((errorItem) => Boolean(errorItem))

        if (errorCount) {
          hasErrors = true
          break
        }
      }
    }

    if (!hasFieldError && !hasErrors) {
      onPublicService()
    }
  }, [description, onCheckServiceTypesTables, onPublicService, serviceName, serviceAbbreviation])

  const onCancelPress = useCallback(() => {
    dispatch(reset())
    dispatch(toggleCancelPressed(true))
    void navigate(-1)
  }, [dispatch, navigate])

  useEffect(() => {
    if (error === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, text.SERVICE_TYPE)) {
      setServiceNameError(getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, text.SERVICE_TYPE))
    }
  }, [error])

  const setServiceTypesForChild = (types: VisitTypeFormState[]) =>
    setServiceTypes(types.map((t) => ({ ...t, ConsentForms: t.ConsentForms ?? [] })))

  const crumbs: Crumb[] = [
    { label: 'Management', onClick: () => void navigate(`/management${locationState?.search ?? ''}`) },
    { label: 'Service types', onClick: () => void navigate('/management?activeTab=Service types') },
    { label: 'New service type' },
  ]

  return (
    <>
      {loading ?
        <LoadingSection />
      : null}
      <EditPageShell
        breadcrumb={<Breadcrumb crumbs={crumbs} />}
        title={
          <h1 className='m-0 text-size-6 font-semibold leading-8 text-text-dark' id='newServiceTypePageTitle'>
            New service type
          </h1>
        }
        constrainMinWidth
        footer={
          <div className='fixed bottom-0 left-[var(--nav-width)] right-0 z-[100] flex items-center justify-end gap-sp-1.5 border-t border-shade-10 bg-white px-sp-3 py-sp-2'>
            <Button priority='secondary' size='m' onClick={onCancelPress}>
              Cancel
            </Button>
            <Button priority='primary' size='m' onClick={onPublishService}>
              Create service type
            </Button>
          </div>
        }>
        <div className='flex flex-col gap-px overflow-hidden rounded-large border border-shade-10 bg-shade-10'>
          <div className='bg-white px-sp-3 py-sp-2.5'>
            <span className='text-size-5 font-semibold leading-7 text-text-dark'>Service information</span>
          </div>
          <div className='bg-white p-sp-3 [&_.view-service-info-container-edit]:mb-0 [&_.view-service-info-container-edit]:rounded-none [&_.view-service-info-container-edit]:border-none [&_.view-service-info-container-edit]:p-0 [&_.view-service-info-label-edit]:hidden'>
            <ServiceInfoForm
              serviceName={serviceName}
              onSetServiceName={onSetServiceName}
              serviceNameError={serviceNameError}
              serviceAbbreviation={serviceAbbreviation}
              onSetServiceAbbreviation={onSetServiceAbbreviation}
              serviceAbbreviationError={serviceAbbreviationError}
              description={description}
              onDescriptionChange={onDescriptionChange}
              descriptionLocalError={descriptionLocalError}
              isDescriptionError={isDescriptionError}
              imageUrl={imageUrl}
            />
          </div>
          {SHOW_SERVICE_SUPPORTED_STATES ?
            <div className='bg-white p-sp-3'>
              <SupportedStatesInteractive excludedStateCodes={excludedStateCodes} onChange={setExcludedStateCodes} />
            </div>
          : null}
        </div>
        <DeleteVisitTypePopup
          isOpen={visitTypeToDelete !== null}
          onClose={() => setVisitTypeToDelete(null)}
          onConfirm={onConfirmDeleteVisitType}
        />
        {serviceTypes.map((t) => (
          <div
            key={t.VisitTypeId}
            data-testid='pw_visit-type-card'
            className='flex flex-col gap-px overflow-hidden rounded-large border border-shade-10 bg-shade-10'>
            <div className='flex items-center justify-between bg-white px-sp-3 py-sp-2.5'>
              <span className='text-size-5 font-semibold leading-7 text-text-dark'>Visit type</span>
              {serviceTypes.length > 1 && (
                <div className='[&_button:hover]:text-text-error [&_button:hover_svg]:text-text-error'>
                  <Button priority='tertiary' size='s' onClick={() => setVisitTypeToDelete(t.VisitTypeId)}>
                    <TrashIcon iconSize='s' />
                    Delete
                  </Button>
                </div>
              )}
            </div>
            <VisitTypeForm
              serviceTypeConfig={t}
              serviceTypes={serviceTypes}
              setServiceTypes={setServiceTypesForChild}
              intakeForms={normalizedIntakeForms}
              additionalConsentFormsOptions={additionalConsentFormsOptions}
            />
          </div>
        ))}
        <div className='flex w-full items-center justify-center rounded-large border-[1.5px] border-dashed border-shade-10 bg-white p-sp-2'>
          <Button priority='tertiary' size='s' onClick={onCreateNewServiceType}>
            <PlusIcon iconSize='s' />
            Add visit type
          </Button>
        </div>
      </EditPageShell>
    </>
  )
}
